/**
 * Switch User Context
 *
 * PE-762: Allows System Admins to "View As User" to test user experiences.
 * Manages the switched user session state across the application.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface SwitchedUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface SwitchUserSession {
  originalUser: SwitchedUser;
  currentUser: SwitchedUser;
  switchedAt: string;
}

interface SwitchUserContextType {
  isSwitched: boolean;
  originalUser: SwitchedUser | null;
  currentUser: SwitchedUser | null;
  switchToUser: (user: SwitchedUser, originalAdmin: SwitchedUser) => void;
  switchBack: () => void;
  getEffectiveRole: (actualRole: string) => string;
}

const SWITCH_USER_SESSION_KEY = 'crm_switched_user_session';

const SwitchUserContext = createContext<SwitchUserContextType | undefined>(undefined);

export const SwitchUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [originalUser, setOriginalUser] = useState<SwitchedUser | null>(null);
  const [currentUser, setCurrentUser] = useState<SwitchedUser | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SWITCH_USER_SESSION_KEY);
      if (saved) {
        const data: SwitchUserSession = JSON.parse(saved);
        setOriginalUser(data.originalUser);
        setCurrentUser(data.currentUser);
        console.log('📋 Restored switched user session:', data.currentUser.name);
      }
    } catch (error) {
      console.error('Failed to restore switch user session:', error);
      localStorage.removeItem(SWITCH_USER_SESSION_KEY);
    }
  }, []);

  const switchToUser = useCallback((user: SwitchedUser, originalAdmin: SwitchedUser) => {
    // Save switch session
    const switchData: SwitchUserSession = {
      originalUser: originalAdmin,
      currentUser: user,
      switchedAt: new Date().toISOString(),
    };

    localStorage.setItem(SWITCH_USER_SESSION_KEY, JSON.stringify(switchData));
    setOriginalUser(originalAdmin);
    setCurrentUser(user);

    console.log(`🔄 Switched to user: ${user.name} (${user.role})`);

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('user-switched', { detail: user }));
  }, []);

  const switchBack = useCallback(() => {
    if (originalUser) {
      console.log(`↩️ Switching back to admin: ${originalUser.name}`);
      localStorage.removeItem(SWITCH_USER_SESSION_KEY);
      setCurrentUser(null);
      setOriginalUser(null);

      // Dispatch event
      window.dispatchEvent(new CustomEvent('user-switched-back'));
    }
  }, [originalUser]);

  // Get effective role considering switch state
  const getEffectiveRole = useCallback((actualRole: string): string => {
    if (originalUser !== null && currentUser !== null) {
      return currentUser.role;
    }
    return actualRole;
  }, [originalUser, currentUser]);

  const isSwitched = originalUser !== null && currentUser !== null;

  return (
    <SwitchUserContext.Provider
      value={{
        isSwitched,
        originalUser,
        currentUser,
        switchToUser,
        switchBack,
        getEffectiveRole,
      }}
    >
      {children}
    </SwitchUserContext.Provider>
  );
};

export const useSwitchUser = () => {
  const context = useContext(SwitchUserContext);
  if (context === undefined) {
    throw new Error('useSwitchUser must be used within SwitchUserProvider');
  }
  return context;
};

export default SwitchUserContext;
